import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FileText, 
  PenTool, 
  User as UserIcon, 
  Settings, 
  BookOpen,
  Sparkles,
  Users,
  ShoppingBag,
  CreditCard
} from 'lucide-react';
import { Tab, User } from '../lib/types';

interface SidebarProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  user?: User;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange, user }) => {
  const location = useLocation();
  
  const menuItems = [
    { id: 'account', label: 'Mon compte', icon: <UserIcon className="w-5 h-5" />, path: '/account' },
    { id: 'posts', label: 'Mes offres', icon: <FileText className="w-5 h-5" />, path: '/dashboard' },
    { id: 'write', label: 'Générer une offre', icon: <PenTool className="w-5 h-5" />, path: '/générer' },
    { id: 'pimper', label: 'Pimper une offre', icon: <Sparkles className="w-5 h-5" />, path: '/pimper' },
    { id: 'bank', label: 'Abonnement', icon: <CreditCard className="w-5 h-5" />, path: '/subscription' },
    { id: 'learn', label: 'Ressources', icon: <BookOpen className="w-5 h-5" />, path: '/resources' }
  ];

  const adminMenuItems = [
    { id: 'users', label: 'Utilisateurs', icon: <Users className="w-5 h-5" />, path: '/admin/users' },
    { id: 'catalogue', label: 'Catalogue', icon: <ShoppingBag className="w-5 h-5" />, path: '/admin/catalogue' },
    { id: 'setup', label: 'Paramètres', icon: <Settings className="w-5 h-5" />, path: '/settings' }
  ];

  const isActive = (item: { id: string; path: string }) => {
    // For items that share the dashboard path, use activeTab
    if (item.path === '/dashboard') {
      return item.id === activeTab;
    }
    
    // For other items, compare with current location
    return location.pathname === item.path;
  };

  return (
    <aside className="bg-gray-50 border-r border-gray-200 w-64 min-h-screen p-4">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-1">Dashboard</h2>
        {user && (
          <p className="text-sm text-gray-500 font-medium">
            {user.name || user.email}
          </p>
        )}
      </div>
      
      <nav>
        <ul className="space-y-1">
          {menuItems.map((item) => (
            <li key={item.id}>
              <Link
                to={item.path}
                onClick={() => onTabChange(item.id as Tab)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item)
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className={isActive(item) ? 'text-indigo-600' : 'text-gray-500'}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </Link>
            </li>
          ))}

          {user?.role === 'admin' && (
            <>
              <li className="pt-4 pb-2">
                <div className="px-4">
                  <div className="h-px bg-gray-200"></div>
                </div>
              </li>
              <li>
                <h3 className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Administration
                </h3>
              </li>
              {adminMenuItems.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive(item)
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className={isActive(item) ? 'text-indigo-600' : 'text-gray-500'}>
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </>
          )}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;